import { captureMessage } from "@sentry/browser";
import { useRouterState } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useRef, useState } from "react";

const HISTORY_STORAGE_KEY = "morty_router_history";

type HistoryManagerContextType = ReturnType<typeof useHistoryManager>;

const HistoryManagerContext = createContext<HistoryManagerContextType>({
  canGoBack: false,
  history: [],
  lastUrl: "",
});

interface HistoryManagerProviderProps {
  children: React.ReactNode;
  manager: HistoryManagerContextType;
}

export function HistoryManagerProvider({
  manager,
  children,
}: HistoryManagerProviderProps) {
  return (
    <HistoryManagerContext.Provider value={manager}>
      {children}
    </HistoryManagerContext.Provider>
  );
}

export default function useHistory() {
  return useContext(HistoryManagerContext);
}

export function useHistoryManager() {
  const currentHref = useRouterState({ select: (s) => s.location.href });
  const currentPathname = useRouterState({
    select: (s) => s.location.pathname,
  });
  const storageReported = useRef(0);
  const prevPathname = useRef(currentPathname);
  const isPopState = useRef(false);
  const [history, setHistory] = useState<string[]>([]);
  const persistEnabled = useRef(false);

  // Read from sessionStorage after mount (client-only)
  useEffect(() => {
    const stored = sessionStorage.getItem(HISTORY_STORAGE_KEY);
    if (stored) {
      setHistory(JSON.parse(stored));
    }
  }, []);

  // Persist to sessionStorage — skip mount render (where history=[])
  useEffect(() => {
    if (!persistEnabled.current) {
      persistEnabled.current = true;
      return;
    }
    sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
  }, [history]);

  // Detect browser back/forward via popstate (fires before router processes)
  useEffect(() => {
    const handlePopState = () => {
      isPopState.current = true;
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Track pathname changes (skip search-param-only changes, equivalent to !shallow)
  useEffect(() => {
    if (currentPathname === prevPathname.current) {
      return;
    }
    prevPathname.current = currentPathname;

    const reportStorage = async () => {
      const estimate = await navigator.storage.estimate();
      const percentage = Math.round(
        estimate.usage && estimate.quota
          ? (estimate.usage / estimate.quota) * 100
          : 0
      );

      if (percentage >= 80) {
        if (storageReported.current === percentage) {
          return;
        }

        storageReported.current = percentage;
        captureMessage("Storage is almost full", {
          extra: {
            percentage,
            quota: `${
              // biome-ignore lint/style/noNonNullAssertion: legacy
              (estimate.quota! / 1024 / 1024).toFixed(2)
            }MB`,
            // biome-ignore lint/style/noNonNullAssertion: legacy
            usage: `${(estimate.usage! / 1024 / 1024).toFixed(2)}MB`,
          },
          level: "warning",
        });
      }
    };

    reportStorage();

    if (isPopState.current) {
      // Browser back/forward — trim 2 entries, add the destination
      setHistory((prev) => [...prev.slice(0, -2), currentHref]);
      isPopState.current = false;
    } else {
      setHistory((prev) => [...prev, currentHref]);
    }
  }, [currentPathname, currentHref]);

  return {
    canGoBack: history.length > 1,
    history,
    lastUrl: [...history].reverse().find((url) => url !== currentHref),
  };
}
